import { Handle, Position } from '@xyflow/react';
import { Stack, Select } from '@mantine/core';
import { IconMessageCircle } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import { VariableTextarea } from '../../components/flow-editor/variable-textarea';
import { RephraseEditor } from '../../components/flow-editor/rephrase-editor';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import { sayWidgetCore, type SayConfig } from '@legacy-shared/widgets/core/say';

export { type SayConfig } from '@legacy-shared/widgets/core/say';

const repeatDigitOptions = [
  { value: '', label: 'Disabled' },
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
  { value: '9', label: '9' },
  { value: '*', label: '* (Star)' },
  { value: '#', label: '# (Pound)' },
];

function getPreviewText(message: string | Record<string, string>): string {
  const text = typeof message === 'string' ? message : (Object.values(message).find((v) => v.trim()) ?? '');
  if (!text) return 'No message configured';
  return text.length > 30 ? `${text.substring(0, 30)}...` : text;
}

function SayNode({ id, data, selected }: WidgetNodeProps<SayConfig>) {
  const preview = getPreviewText(data.config.message);

  return (
    <WidgetNodeShell
      icon={IconMessageCircle}
      title={data.label || 'Say'}
      subtitle={preview}
      configured={
        typeof data.config.message === 'string'
          ? !!data.config.message
          : Object.values(data.config.message).some((v) => v.trim())
      }
      errors={data.errors}
      selected={selected}
      color="indigo"
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="out" label="Next" offset="50%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function SayConfigPanel({
  config,
  onChange,
  availableVariables,
  zeroRouteEnabled,
  languages,
  defaultLanguage,
}: ConfigPanelProps<SayConfig>) {
  const digitOptions = zeroRouteEnabled ? repeatDigitOptions.filter((d) => d.value !== '0') : repeatDigitOptions;

  return (
    <Stack gap="md" h="100%">
      <Select
        label="Repeat Digit"
        description="Allow the caller to press this digit to hear the message again"
        value={config.repeatDigit ?? ''}
        onChange={(v) => onChange({ ...config, repeatDigit: v || undefined })}
        data={digitOptions}
      />
      <VariableTextarea
        label="Message"
        description="The text to speak to the caller"
        placeholder="Hello, welcome to our service."
        value={config.message}
        onChange={(message) => onChange({ ...config, message })}
        availableVariables={availableVariables}
        languages={languages}
        defaultLanguage={defaultLanguage}
        minRows={3}
        style={{ flex: 1 }}
        styles={{
          root: { display: 'flex', flexDirection: 'column', flex: 1 },
          wrapper: { flex: 1 },
          input: { height: '100%' },
        }}
      />
      <RephraseEditor
        message={config.message}
        rephrases={config.rephrases}
        onChange={(rephrases) => onChange({ ...config, rephrases })}
        languages={languages}
        defaultLanguage={defaultLanguage}
      />
    </Stack>
  );
}

export const sayWidget: WidgetDefinition<SayConfig> = {
  ...sayWidgetCore,
  icon: 'IconMessageCircle',

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [{ id: 'out', type: 'default', label: 'Next', position: 'bottom' }],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: SayNode,
    configPanel: SayConfigPanel,
    preview: (config) => getPreviewText(config.message),
  },

  validation: {
    ...sayWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
